import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { legalDocumentsApi, authApi, ApiError } from '@/lib/pillarApi';
import { isPillarApiUnreachableError } from '@pillar-portal/api-types';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import {
    invalidateSessionSensitiveRequests,
    StaleEffectiveSessionResponseError,
} from '@/lib/sessionSensitiveRequestManager';
import { buildRequiredTotpSetupPath } from '@/lib/requiredTotpSetup';
import {
    buildCloverOAuthLoginPath,
    parseCloverOAuthLaunchContext,
} from '@/lib/clover/oauth-launch-context';
import { runInBackground } from '@/lib/utils';
import { completeSambaPortalLogout } from '@/lib/sambaNativeRuntime';
import { useAuthStore, type EffectiveSessionGeneration } from '@/store/auth';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    ActiveRoleAssignmentOperationalResponse,
    ActiveRoleAssignmentResponse,
    getAllRoleAssignments,
    ImpersonationContextResponse,
    SessionUserProfileDetailedResponse,
    SessionUserProfileDetailedResponseSchema,
    ApplicableTermsResult,
} from '@pillar-portal/api-types';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireAuth?: boolean;
}

const PUBLIC_EXACT_ROUTES = [
    '/login',
    '/login/password',
    '/login/passwordless',
    '/login/totp',
    '/login/mfa-setup',
    '/forgot-password',
];

const PUBLIC_ROUTE_PREFIXES = ['/reset-password/', '/verify-email/'];

// Shown when the backend did not answer, so a connection problem is not
// mistaken for an account or legal-configuration problem (BUG-312).
const SERVER_UNREACHABLE_MESSAGE =
    'The Anchorbase server could not be reached. Check your connection, then retry.';

function resolveActiveRoleAssignmentId(
    activeRoleAssignment: ActiveRoleAssignmentResponse | null,
    user: SessionUserProfileDetailedResponse | null
): number | null {
    if (activeRoleAssignment === null || user === null) {
        return null;
    }
    return (
        getAllRoleAssignments(user).find(
            (roleAssignment) =>
                roleAssignment.role_assignment_uuid === activeRoleAssignment.role_assignment_uuid
        )?.role_assignment_id ?? null
    );
}

export function ProtectedRoute({ children, requireAuth = true }: ProtectedRouteProps) {
    const log = createComponentLogger('ProtectedRoute');
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const loginPath: string =
        pathname === '/clover/launch'
            ? (() => {
                  const context = parseCloverOAuthLaunchContext(
                      new URLSearchParams(location.search)
                  );
                  return context === null ? '/login' : buildCloverOAuthLoginPath(context);
              })()
            : '/login';
    const queryClient = useQueryClient();

    const {
        user,
        setUserForEffectiveSessionGeneration,
        setActiveRoleAssignmentIdForEffectiveSessionGeneration,
        getEffectiveSessionGeneration,
        logout: logoutStore,
        isLoggingOut,
        setIsLoggingOut,
        logoutFailure,
        setLogoutFailure,
    } = useAuthStore();
    const storedActiveRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const [processedActiveRoleAssignment, setProcessedActiveRoleAssignment] = useState<
        ActiveRoleAssignmentResponse | null | undefined
    >(undefined);
    const [isMounted, setIsMounted] = useState(false);
    const profileGenerationRef = useRef<EffectiveSessionGeneration>(
        getEffectiveSessionGeneration()
    );
    const activeRoleAssignmentGenerationRef = useRef<EffectiveSessionGeneration>(
        getEffectiveSessionGeneration()
    );

    const isPublicRoute = pathname
        ? PUBLIC_EXACT_ROUTES.includes(pathname) ||
          PUBLIC_ROUTE_PREFIXES.some((route) => pathname.startsWith(route))
        : false;
    const requiresPasswordChange: boolean = user?.requires_password_change === true;
    const isPasswordChangeRoute: boolean = pathname.startsWith('/profile/password');

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Cleanup the isLoggingOut flag when navigating to a public route
    useEffect(() => {
        if (isPublicRoute && isLoggingOut) {
            setIsLoggingOut(false);
        }
    }, [isPublicRoute, isLoggingOut, setIsLoggingOut]);

    // Cancel and reset profile query
    // Only remove queries on public routes or when explicitly logging out
    // Don't remove queries when !user on protected routes - that's normal state before profile fetch completes
    useEffect(() => {
        if (isPublicRoute || isLoggingOut) {
            runInBackground(queryClient.cancelQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error canceling queries', err);
            });
            runInBackground(queryClient.removeQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error removing queries', err);
            });
        }
    }, [isPublicRoute, isLoggingOut, queryClient]);

    const isQueryEnabled = requireAuth && isMounted && !isPublicRoute && !user && !isLoggingOut;

    const {
        data: profileData,
        error,
        isLoading,
        isFetched, // Used to track if the profile check has ever completed
        refetch: refetchProfile,
    } = useQuery<SessionUserProfileDetailedResponse | null>({
        queryKey: ['profile'],
        queryFn: async () => {
            const response = await sessionSensitiveAuthApi.getProfile({
                handle401AsSessionExpired: false,
            });
            if (response === null || response === undefined) {
                return null;
            }
            profileGenerationRef.current = getEffectiveSessionGeneration();
            return SessionUserProfileDetailedResponseSchema.parse(response);
        },
        retry: (failureCount, queryError) =>
            queryError instanceof StaleEffectiveSessionResponseError && failureCount < 1,
        retryDelay: 0,
        enabled: isQueryEnabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true, // Always refetch on mount to check auth status
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (profileData) {
            setUserForEffectiveSessionGeneration(profileData, profileGenerationRef.current);
        }
    }, [profileData, setUserForEffectiveSessionGeneration]);

    const isProfileAccessForbidden: boolean = error instanceof ApiError && error.status === 403;
    const isProfileAuthenticationFailure: boolean =
        error instanceof ApiError && error.status === 401;
    const isProfileRefreshFailure: boolean =
        error !== null &&
        error !== undefined &&
        !isProfileAccessForbidden &&
        !isProfileAuthenticationFailure;
    const reportedProfileRefreshErrorRef = useRef<unknown>(null);

    useEffect(() => {
        if (!isProfileRefreshFailure || reportedProfileRefreshErrorRef.current === error) {
            return;
        }
        reportedProfileRefreshErrorRef.current = error;
        log.error('Effective session refresh failed', error, {
            effective_session_generation: getEffectiveSessionGeneration().value,
            failed_request_category: 'profile',
            recovery_action: 'user_retry',
        });
    }, [error, getEffectiveSessionGeneration, isProfileRefreshFailure, log]);

    const isActiveRoleAssignmentQueryEnabled: boolean =
        requireAuth &&
        isMounted &&
        !isPublicRoute &&
        (!!user || isProfileAccessForbidden) &&
        !requiresPasswordChange &&
        !isLoggingOut;

    const {
        data: activeRoleAssignment,
        isLoading: isActiveRoleAssignmentLoading,
        isFetched: isActiveRoleAssignmentFetched,
    } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            activeRoleAssignmentGenerationRef.current = getEffectiveSessionGeneration();
            return await sessionSensitiveAuthApi.getActiveRoleAssignment();
        },
        retry: false,
        enabled: isActiveRoleAssignmentQueryEnabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (!isActiveRoleAssignmentFetched || activeRoleAssignment === undefined) {
            return;
        }
        const activeRoleAssignmentId: number | null = resolveActiveRoleAssignmentId(
            activeRoleAssignment,
            user
        );
        setActiveRoleAssignmentIdForEffectiveSessionGeneration(
            activeRoleAssignmentId,
            activeRoleAssignmentGenerationRef.current
        );
        setProcessedActiveRoleAssignment(activeRoleAssignment);
    }, [
        activeRoleAssignment,
        isActiveRoleAssignmentFetched,
        setActiveRoleAssignmentIdForEffectiveSessionGeneration,
        user,
    ]);

    const isLegalPartialSession: boolean =
        activeRoleAssignment?.activation_state === 'legal_acceptance_required';
    const hasProtectedSessionContext: boolean = !!user || isLegalPartialSession;

    const isImpersonationContextQueryEnabled: boolean =
        requireAuth &&
        isMounted &&
        !isPublicRoute &&
        !!user &&
        !requiresPasswordChange &&
        !isLoggingOut;

    const {
        data: impersonationContext,
        isLoading: isImpersonationContextLoading,
        isFetched: isImpersonationContextFetched,
    } = useQuery<ImpersonationContextResponse | null>({
        queryKey: ['impersonationContext'],
        queryFn: async () => {
            return await sessionSensitiveAuthApi.getImpersonationContext();
        },
        retry: false,
        enabled: isImpersonationContextQueryEnabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
    });

    const isApplicableTermsQueryEnabled: boolean =
        requireAuth &&
        isMounted &&
        !isPublicRoute &&
        hasProtectedSessionContext &&
        !requiresPasswordChange &&
        isActiveRoleAssignmentFetched &&
        !isActiveRoleAssignmentLoading &&
        activeRoleAssignment !== null &&
        (user === null || (isImpersonationContextFetched && impersonationContext === null)) &&
        !isLoggingOut;

    const {
        data: applicableTerms,
        error: applicableTermsError,
        isLoading: isApplicableTermsLoading,
        isFetched: isApplicableTermsFetched,
        refetch: refetchApplicableTerms,
    } = useQuery<ApplicableTermsResult>({
        queryKey: ['applicableTerms'],
        queryFn: async () => await legalDocumentsApi.getApplicableTerms(),
        retry: false,
        enabled: isApplicableTermsQueryEnabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (!requireAuth || isPublicRoute || isLoggingOut) {
            return;
        }
        if (!requiresPasswordChange) {
            return;
        }
        if (isPasswordChangeRoute) {
            return;
        }
        runInBackground(navigate('/profile/password', { replace: true }), (err) => {
            log.error('Error navigating to password change', err);
        });
    }, [
        requireAuth,
        isPublicRoute,
        isLoggingOut,
        requiresPasswordChange,
        isPasswordChangeRoute,
        navigate,
        log,
    ]);

    useEffect(() => {
        if (!requireAuth || isPublicRoute || isLoggingOut) {
            return;
        }
        if (requiresPasswordChange) {
            return;
        }
        if (!hasProtectedSessionContext) {
            return;
        }
        if (user !== null && (!isImpersonationContextFetched || impersonationContext !== null)) {
            return;
        }
        if (!isApplicableTermsFetched || isApplicableTermsLoading) {
            return;
        }
        if (!isLegalPartialSession && applicableTerms?.coverage_state !== 'acceptance_required') {
            return;
        }
        if (pathname.startsWith('/legal-workflow')) {
            return;
        }
        const returnTo: string = encodeURIComponent(pathname);
        runInBackground(
            navigate(`/legal-workflow?returnTo=${returnTo}`, { replace: true }),
            (err) => {
                log.error('Error navigating to legal workflow', err);
            }
        );
    }, [
        requireAuth,
        isPublicRoute,
        isLoggingOut,
        requiresPasswordChange,
        hasProtectedSessionContext,
        user,
        isImpersonationContextFetched,
        impersonationContext,
        isLegalPartialSession,
        isApplicableTermsFetched,
        isApplicableTermsLoading,
        applicableTerms,
        pathname,
        navigate,
        log,
    ]);

    useEffect(() => {
        if (
            !requireAuth ||
            isPublicRoute ||
            isLoggingOut ||
            !isImpersonationContextFetched ||
            impersonationContext === null ||
            !pathname.startsWith('/legal-workflow')
        ) {
            return;
        }
        runInBackground(navigate('/dashboard', { replace: true }), (err) => {
            log.error('Unable to leave legal workflow during impersonation', err);
        });
    }, [
        requireAuth,
        isPublicRoute,
        isLoggingOut,
        isImpersonationContextFetched,
        impersonationContext,
        pathname,
        navigate,
        log,
    ]);

    useEffect(() => {
        if (applicableTermsError === null) {
            return;
        }
        log.error('Applicable legal terms query failed', applicableTermsError);
    }, [applicableTermsError, log]);

    const hasConfiguredMfaMethod: boolean =
        user?.totp_enabled === true ||
        user?.mfa_email_allowed === true ||
        user?.mfa_sms_allowed === true;
    const resolvedActiveRoleAssignment: ActiveRoleAssignmentOperationalResponse | null =
        activeRoleAssignment?.activation_state === 'operational' ? activeRoleAssignment : null;
    const requiresMandatoryTotpSetup: boolean =
        impersonationContext === null &&
        !hasConfiguredMfaMethod &&
        resolvedActiveRoleAssignment?.combined_config.org_config.platformConfig?.require2FA ===
            true;

    useEffect(() => {
        if (!requireAuth || isPublicRoute || isLoggingOut) {
            return;
        }
        if (requiresPasswordChange) {
            return;
        }
        if (!user) {
            return;
        }
        if (!isActiveRoleAssignmentFetched || isActiveRoleAssignmentLoading) {
            return;
        }
        if (!isImpersonationContextFetched || isImpersonationContextLoading) {
            return;
        }
        if (!requiresMandatoryTotpSetup) {
            return;
        }
        if (pathname.startsWith('/login/mfa-setup')) {
            return;
        }
        runInBackground(
            navigate(
                buildRequiredTotpSetupPath(
                    resolvedActiveRoleAssignment === null
                        ? undefined
                        : {
                              role_assignment_uuid:
                                  resolvedActiveRoleAssignment.role_assignment_uuid,
                              organization_uuid: resolvedActiveRoleAssignment.organization_uuid,
                              organization_name: resolvedActiveRoleAssignment.organization_name,
                          }
                ),
                { replace: true }
            ),
            (err) => {
                log.error('Error navigating to mandatory TOTP setup', err);
            }
        );
    }, [
        requireAuth,
        isPublicRoute,
        isLoggingOut,
        requiresPasswordChange,
        user,
        isActiveRoleAssignmentFetched,
        isActiveRoleAssignmentLoading,
        isImpersonationContextFetched,
        isImpersonationContextLoading,
        requiresMandatoryTotpSetup,
        pathname,
        resolvedActiveRoleAssignment,
        navigate,
        log,
    ]);

    // Handle invalid session: 401 Unauthorized
    useEffect(() => {
        if (
            requireAuth &&
            !isLoggingOut &&
            !isPublicRoute &&
            pathname !== '/login' &&
            error &&
            error instanceof ApiError &&
            error.status === 401
        ) {
            setIsLoggingOut(true);
            runInBackground(queryClient.clear(), (err) => {
                log.error('Error clearing queries', err);
            });
            runInBackground(queryClient.removeQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error removing queries', err);
            });
            logoutStore();
            invalidateSessionSensitiveRequests();
            runInBackground(navigate(loginPath, { replace: true }), (err) => {
                log.error('Error navigating to login', err);
            });
        }
    }, [
        error,
        isLoggingOut,
        requireAuth,
        isPublicRoute,
        pathname,
        logoutStore,
        navigate,
        queryClient,
        setIsLoggingOut,
        loginPath,
    ]);

    useEffect(() => {
        // Only redirect if:
        // 1. Query is/was enabled (we're on a protected route and user is null)
        // 2. Query has completed (isFetched is true AND not currently loading)
        // 3. No profile data (query failed or returned no data)
        // 4. No user was set (profile fetch failed or returned no user)
        // 5. Not currently logging out
        // 6. A profile 403 has finished checking for a legal-partial session
        const isResolvingLegalPartialSession: boolean =
            isProfileAccessForbidden &&
            (!isActiveRoleAssignmentFetched || isActiveRoleAssignmentLoading);
        const shouldRedirectToLogin =
            requireAuth &&
            !isPublicRoute &&
            !hasProtectedSessionContext &&
            !profileData &&
            !isLoggingOut &&
            isQueryEnabled &&
            isFetched &&
            !isLoading &&
            !isResolvingLegalPartialSession &&
            (!error || isProfileAccessForbidden) &&
            !(error && error instanceof ApiError && error.status === 401) &&
            !(error instanceof StaleEffectiveSessionResponseError);

        if (shouldRedirectToLogin) {
            runInBackground(navigate(loginPath, { replace: true }), (err) => {
                log.error('Error navigating to login', err);
            });
        }
    }, [
        requireAuth,
        isPublicRoute,
        hasProtectedSessionContext,
        profileData,
        isLoggingOut,
        isQueryEnabled,
        isFetched,
        isLoading,
        isProfileAccessForbidden,
        isActiveRoleAssignmentFetched,
        isActiveRoleAssignmentLoading,
        error,
        navigate,
        loginPath,
    ]);

    // --- RENDERING LOGIC ---

    // Don't block rendering on public routes (render immediately)
    if (isPublicRoute) {
        return <>{children}</>;
    }

    if (isLoggingOut) {
        return (
            <div
                className="min-h-screen flex flex-col items-center justify-center gap-3"
                role={logoutFailure === null ? 'status' : 'alert'}
            >
                <p>
                    {logoutFailure === null
                        ? 'Completing logout...'
                        : 'Logout did not finish. Try again.'}
                </p>
                {logoutFailure !== null && (
                    <button
                        type="button"
                        className="rounded border px-3 py-2"
                        onClick={() => {
                            let serverLogoutFailed = logoutFailure === 'server';
                            setLogoutFailure(null);
                            runInBackground(
                                (async () => {
                                    if (serverLogoutFailed) {
                                        try {
                                            await authApi.logout();
                                            serverLogoutFailed = false;
                                        } catch (error) {
                                            log.error('Unable to retry server Logout', error);
                                        }
                                    }
                                    await completeSambaPortalLogout();
                                    await navigate(loginPath, { replace: true });
                                })(),
                                (error: unknown) => {
                                    log.error('Unable to retry native Logout cleanup', error);
                                    setLogoutFailure(serverLogoutFailed ? 'server' : 'native');
                                }
                            );
                        }}
                    >
                        Retry Logout
                    </button>
                )}
            </div>
        );
    }

    // Role-scoped queries key on the stored active role assignment id, so protected
    // content waits while the stored id differs from the fetched assignment's and
    // the effect above has not yet handled it.
    // Rendering earlier starts every role-scoped query under the old id and fetches
    // it again under the new one (BUG-302).
    const isActiveRoleAssignmentIdPending: boolean =
        isActiveRoleAssignmentFetched &&
        activeRoleAssignment !== undefined &&
        processedActiveRoleAssignment !== activeRoleAssignment &&
        storedActiveRoleAssignmentId !== resolveActiveRoleAssignmentId(activeRoleAssignment, user);

    // Show loading state if auth is required and we are waiting for initial mount OR fetch
    if (
        requireAuth &&
        (!isMounted ||
            isLoading ||
            isApplicableTermsLoading ||
            isActiveRoleAssignmentLoading ||
            isImpersonationContextLoading ||
            (!isPublicRoute && isActiveRoleAssignmentIdPending))
    ) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Loading...</p>
            </div>
        );
    }

    if (requireAuth && !isPublicRoute && !hasProtectedSessionContext && isProfileRefreshFailure) {
        return (
            <div
                className="min-h-screen flex flex-col items-center justify-center gap-3"
                role="alert"
            >
                {isPillarApiUnreachableError(error) ? (
                    <p>{SERVER_UNREACHABLE_MESSAGE}</p>
                ) : (
                    <>
                        <p>Your session could not be refreshed.</p>
                        {error instanceof ApiError && error.responseMessage && (
                            <p>{error.responseMessage}</p>
                        )}
                    </>
                )}
                <p>Your work is still signed in. Try loading your session again.</p>
                <button
                    type="button"
                    className="rounded border px-3 py-2"
                    onClick={() => {
                        reportedProfileRefreshErrorRef.current = null;
                        runInBackground(refetchProfile(), (refetchError: unknown) => {
                            log.error('Unable to retry effective session refresh', refetchError, {
                                effective_session_generation: getEffectiveSessionGeneration().value,
                                failed_request_category: 'profile',
                                recovery_action: 'user_retry',
                            });
                        });
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    if (requireAuth && !isPublicRoute && !hasProtectedSessionContext) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Refreshing your session...</p>
            </div>
        );
    }

    if (
        requireAuth &&
        !isPublicRoute &&
        isImpersonationContextFetched &&
        impersonationContext !== null &&
        pathname.startsWith('/legal-workflow')
    ) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Returning to the application...</p>
            </div>
        );
    }

    if (requireAuth && !isPublicRoute && user && requiresPasswordChange && !isPasswordChangeRoute) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Redirecting to password change...</p>
            </div>
        );
    }

    if (
        requireAuth &&
        !isPublicRoute &&
        user &&
        requiresMandatoryTotpSetup &&
        !pathname.startsWith('/login/mfa-setup')
    ) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Redirecting to security settings...</p>
            </div>
        );
    }

    if (
        requireAuth &&
        !isPublicRoute &&
        hasProtectedSessionContext &&
        (isLegalPartialSession || applicableTerms?.coverage_state === 'acceptance_required') &&
        !pathname.startsWith('/legal-workflow')
    ) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Redirecting to legal workflow...</p>
            </div>
        );
    }

    if (
        requireAuth &&
        !isPublicRoute &&
        hasProtectedSessionContext &&
        isApplicableTermsFetched &&
        applicableTermsError !== null &&
        !pathname.startsWith('/legal-workflow')
    ) {
        return (
            <div
                className="min-h-screen flex flex-col items-center justify-center gap-3"
                role="alert"
            >
                <p>
                    {isPillarApiUnreachableError(applicableTermsError)
                        ? SERVER_UNREACHABLE_MESSAGE
                        : 'Legal access requirements could not be verified.'}
                </p>
                <button
                    type="button"
                    className="rounded border px-3 py-2"
                    onClick={() => {
                        runInBackground(refetchApplicableTerms(), (refetchError: unknown) => {
                            log.error('Unable to retry applicable legal terms', refetchError);
                        });
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    // If we've passed all checks (auth required, loading done, user is present), render children
    return <>{children}</>;
}
